import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from "react";
import { useSuspenseQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { clickCta } from "@/lib/analytics";
import { cn } from "@/lib/utils";
import { projectsQueryOptions } from "@/lib/portfolio";
import type { Project } from "@/lib/portfolio";
import { ProjectCard, Lightbox } from "./SelectedWork";


interface WorkMarqueeProps {
  sectionId?: string;
  className?: string;
}

// Measure before paint on the client so cards never flash at the wrong width.
const useIsomorphicLayoutEffect =
  typeof window !== "undefined" ? useLayoutEffect : useEffect;

function Row({
  projects,
  reverse,
  onLightbox,
  duration,
  perView,
  visible,
  scrollable = false,
}: {
  projects: Project[];
  reverse?: boolean;
  onLightbox: (p: Project) => void;
  duration: string;
  perView: number;
  visible: number;
  scrollable?: boolean;
}) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const [setWidth, setSetWidth] = useState<number | null>(null);
  // Arrow nudges: glide to a target position instead of snapping.
  const targetRef = useRef(0);
  const posRef = useRef(0);
  const nudgingRef = useRef(false);
  const nudgeStartRef = useRef(0);
  const nudgeFromRef = useRef(0);

  useIsomorphicLayoutEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const update = () => setSetWidth(el.clientWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // The row viewport is stationary even while its cards move. Keep every video
  // node mounted, and manage playback without React state so crossing the center
  // or the loop seam can never replace a playing video with its poster.
  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport || projects.length === 0) return;

    const phoneQuery = window.matchMedia("(max-width: 1023px)");
    let rowVisible = false;

    const syncPlayback = () => {
      const videos = Array.from(
        viewport.querySelectorAll<HTMLVideoElement>("video[data-portfolio-cover]"),
      );
      if (!rowVisible || document.visibilityState !== "visible") {
        videos.forEach((video) => video.pause());
        return;
      }

      if (!phoneQuery.matches) {
        videos.forEach((video) => {
          video.muted = true;
          if (video.paused) void video.play().catch(() => undefined);
        });
        return;
      }

      const viewportRect = viewport.getBoundingClientRect();
      const center = viewportRect.left + viewportRect.width / 2;
      const ranked = videos
        .map((video) => {
          const rect = video.getBoundingClientRect();
          const intersects = rect.right > viewportRect.left && rect.left < viewportRect.right;
          const distance = Math.abs(rect.left + rect.width / 2 - center);
          return { video, intersects, distance };
        })
        .filter((entry) => entry.intersects)
        .sort((a, b) => a.distance - b.distance);
      // One visible player plus its nearest handoff neighbour per row keeps iOS
      // decoder use bounded without ever unmounting or changing a source.
      const active = new Set(ranked.slice(0, 2).map((entry) => entry.video));
      videos.forEach((video) => {
        if (active.has(video)) {
          video.muted = true;
          if (video.paused) void video.play().catch(() => undefined);
        } else {
          video.pause();
        }
      });
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        rowVisible = Boolean(entry?.isIntersecting);
        syncPlayback();
      },
      { rootMargin: "160px 0px", threshold: 0 },
    );
    observer.observe(viewport);

    const interval = window.setInterval(syncPlayback, 250);
    document.addEventListener("visibilitychange", syncPlayback);
    phoneQuery.addEventListener("change", syncPlayback);
    return () => {
      observer.disconnect();
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", syncPlayback);
      phoneQuery.removeEventListener("change", syncPlayback);
    };
  }, [projects.length]);

  // Drive the row with a single rAF loop on all viewports. This gives us
  // smooth arrow nudges, seamless looping, and consistent pixels-per-second.
  const NUDGE_DURATION = 400;
  useEffect(() => {
    const viewport = viewportRef.current;
    const track = trackRef.current;
    if (!viewport || !track || projects.length === 0) return;

    let raf = 0;
    let last = 0;
    let pausedUntil = 0;
    let userScrolling = false;

    // One sequence = every project once; the track repeats it, so wrapping by
    // this distance is seamless regardless of how many repeats are rendered.
    const seq = () => {
      const byLayout = setWidth ? (setWidth * projects.length) / visible : 0;
      return byLayout > 0 ? byLayout : track.scrollWidth / 2;
    };
    const half = seq;

    const speed = () => {
      const s = seq();
      const durationSec = parseFloat(duration) || 0;
      if (s > 0 && durationSec > 0) return s / durationSec;
      return 65;
    };

    if (reverse) {
      posRef.current = targetRef.current = half();
      viewport.scrollLeft = posRef.current;
    }

    const step = (now: number) => {
      const dt = last ? Math.min(now - last, 50) : 0;
      last = now;
      const h = half();
      if (h <= 0) {
        raf = requestAnimationFrame(step);
        return;
      }

      if (userScrolling) {
        posRef.current = targetRef.current = viewport.scrollLeft;
        userScrolling = false;
      }

      if (nudgingRef.current) {
        const elapsed = now - nudgeStartRef.current;
        const t = Math.min(elapsed / NUDGE_DURATION, 1);
        const eased = 1 - Math.pow(1 - t, 3);
        posRef.current = nudgeFromRef.current + (targetRef.current - nudgeFromRef.current) * eased;
        if (t >= 1) {
          nudgingRef.current = false;
          pausedUntil = now + 1200;
        }
      } else if (now > pausedUntil) {
        const sp = speed();
        targetRef.current += (reverse ? -1 : 1) * (sp / 1000) * dt;
        posRef.current = targetRef.current;
      }

      viewport.scrollLeft = ((posRef.current % h) + h) % h;
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);

    const pause = () => {
      pausedUntil = performance.now() + 1200;
      userScrolling = true;
    };
    viewport.addEventListener("touchstart", pause, { passive: true });
    viewport.addEventListener("touchmove", pause, { passive: true });
    viewport.addEventListener("touchend", pause, { passive: true });

    const handleMouseEnter = () => {
      pausedUntil = performance.now() + 100000;
    };
    const handleMouseLeave = () => {
      pausedUntil = performance.now() + 300;
    };
    viewport.addEventListener("mouseenter", handleMouseEnter);
    viewport.addEventListener("mouseleave", handleMouseLeave);

    return () => {
      cancelAnimationFrame(raf);
      viewport.removeEventListener("touchstart", pause);
      viewport.removeEventListener("touchmove", pause);
      viewport.removeEventListener("touchend", pause);
      viewport.removeEventListener("mouseenter", handleMouseEnter);
      viewport.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, [reverse, setWidth, projects.length, perView, visible, duration]);


  if (projects.length === 0) return null;

  // No padding/repeats — a short last chunk keeps its own width so items never duplicate.
  const chunks: Project[][] = [];
  for (let i = 0; i < projects.length; i += perView) {
    chunks.push(projects.slice(i, i + perView));
  }

  // One "sequence" = every project once. Repeat only enough to cover the
  // viewport plus one handoff sequence, then animate by exactly one sequence so short rows
  // (e.g. a single Ultra project) loop seamlessly instead of snapping back.
  const seqWidth =
    setWidth !== null ? (setWidth * projects.length) / visible : null;
  const repeats =
    seqWidth && seqWidth > 0 && setWidth !== null
      ? Math.max(2, Math.ceil(setWidth / seqWidth) + 1)
      : 1;

  // Before measurement the track must have a definite width, otherwise the
  // percentage group widths below resolve against max-content and each card
  // blows up to the intrinsic size of its cover image.
  const trackClass = setWidth !== null ? "flex shrink-0 w-max" : "flex shrink-0 w-full";


  // One card step in pixels — the distance an arrow click glides the row.
  const cardStep = setWidth !== null ? setWidth / visible : 0;

  const nudge = (dir: 1 | -1) => {
    const h = seqWidth || 1;
    const step = cardStep || h / Math.max(projects.length, 1);
    nudgeFromRef.current = posRef.current;
    targetRef.current += dir * step;
    nudgeStartRef.current = performance.now();
    nudgingRef.current = true;
  };

  const trackStyle: CSSProperties = {
    WebkitBackfaceVisibility: "hidden",
    backfaceVisibility: "hidden" as const,
  };

  const arrowClass =
    "absolute top-1/2 z-10 hidden h-10 w-10 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-white text-gray-950 opacity-0 shadow-md ring-1 ring-black/10 transition-all duration-200 hover:scale-105 hover:shadow-lg group-hover/rowwrap:opacity-100 focus-visible:opacity-100 sm:flex";

  return (
    <div className="group/rowwrap relative overflow-x-clip py-2">
      <button
        type="button"
        aria-label="Previous projects"
        onClick={() => nudge(-1)}
        className={cn(arrowClass, "-left-3 lg:-left-5 xl:-left-7")}
      >
        <ChevronLeft className="h-5 w-5" />
      </button>
      <button
        type="button"
        aria-label="Next projects"
        onClick={() => nudge(1)}
        className={cn(arrowClass, "-right-3 lg:-right-5 xl:-right-7")}
      >
        <ChevronRight className="h-5 w-5" />
      </button>
      <div
        ref={viewportRef}
        className={cn(
          "group/row -my-2 flex px-3 py-5",
          scrollable ? "overflow-x-auto [&::-webkit-scrollbar]:hidden" : "overflow-hidden",
        )}
        style={
          scrollable
            ? { scrollbarWidth: "none", msOverflowStyle: "none", touchAction: "pan-x pan-y", overscrollBehaviorX: "contain" }
            : undefined
        }
      >

      <div
        ref={trackRef}
        className={cn("flex shrink-0", trackClass)}
        style={trackStyle}
      >
        {Array.from({ length: repeats }, (_, dup) =>
          chunks.map((chunk, ci) => (
            <div
              key={`${dup}-${ci}`}
              className="grid shrink-0 gap-3 pr-3 sm:gap-7 sm:pr-7 lg:gap-8 lg:pr-8 xl:gap-10 xl:pr-10"
              style={{
                gridTemplateColumns: `repeat(${chunk.length}, minmax(0, 1fr))`,
                // Before measurement, percentages of the full-width track already
                // give the right cards-per-view so nothing resizes after hydration.
                width:
                  setWidth !== null
                    ? `${(setWidth * chunk.length) / visible}px`
                    : `${(chunk.length / visible) * 100}%`,
              }}
              aria-hidden={dup > 0}
            >

              {chunk.map((p, pi) => (
                <div key={`${dup}-${ci}-${pi}-${p.slug}`} className="min-w-0 max-w-full px-0.5 pb-1">
                  <ProjectCard project={p} onLightbox={() => onLightbox(p)} hideTier />
                </div>
              ))}



            </div>
          )),
        )}


      </div>
      </div>
    </div>
  );
}



function SkeletonRow() {
  return (
    <div className="grid grid-cols-2 gap-5 sm:grid-cols-3 sm:gap-7 lg:gap-8 xl:gap-10" aria-busy="true" aria-label="Loading projects">
      {Array.from({ length: 3 }).map((_, i) => (
        <div
          key={i}
          className={cn(
            "overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-black/5",
            i >= 2 && "hidden sm:block",
          )}
        >
          <div className="aspect-[4/3] w-full animate-pulse bg-gray-100" />
          <div className="bg-[#f3f6fa] p-4 sm:p-5">
            <div className="h-4 w-3/4 animate-pulse rounded bg-gray-100" />
            <div className="mt-2 h-3 w-1/2 animate-pulse rounded bg-gray-100" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function WorkMarquee({ sectionId = "work", className }: WorkMarqueeProps = {}) {
  const { data: projects } = useSuspenseQuery(projectsQueryOptions);
  const [lightbox, setLightbox] = useState<Project | null>(null);
  // null = viewport class not measured yet (SSR / first hydration frame).
  const [isMobile, setIsMobile] = useState<boolean | null>(null);
  const queryClient = useQueryClient();
  const isEmpty = projects.length === 0;

  useEffect(() => {
    if (!isEmpty) return;
    queryClient.invalidateQueries({ queryKey: projectsQueryOptions.queryKey });
  }, [isEmpty, queryClient]);

  // Measured before the first client paint so the row never renders with the
  // desktop cards-per-view and then snaps to the mobile layout.
  useIsomorphicLayoutEffect(() => {
    const mq = window.matchMedia("(max-width: 639px)");
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  // Manually curated in WordPress via the "Feature on home page" checkbox.
  // Split all featured projects across both rows: up to 16 total.
  const handpicked = projects
    .filter((p) => p.featured)
    .sort((a, b) => (a.homeOrder ?? Infinity) - (b.homeOrder ?? Infinity));
  const topRow = handpicked.slice(0, 8);
  const bottomRow = handpicked.slice(8, 16);
  // Nothing curated yet — keep the previous newest-projects fallback on the top row.
  const fallbackTop = topRow.length === 0 && bottomRow.length === 0 ? projects.slice(0, 4) : topRow;

  // Every row must scroll at the same pixels-per-second: the animation always
  // travels one full set of cards, so the duration has to scale with the count.
  const secondsPerCard = isMobile ? 5.5 : 7.5;
  const rowDuration = (count: number) => `${Math.max(count, 1) * secondsPerCard}s`;



  return (
    <section id={sectionId} className={cn("bg-background pt-10 pb-8 sm:pt-14 sm:pb-10", className)}>
      <div className="mx-auto max-w-7xl px-2 sm:px-6 lg:px-10 xl:px-14">
        <div className="flex flex-col gap-0">
          {isEmpty || isMobile === null ? (
            <>
              <SkeletonRow />
              <SkeletonRow />
            </>
          ) : (
            <>
              <Row projects={fallbackTop} onLightbox={setLightbox} duration={rowDuration(fallbackTop.length)} perView={isMobile ? 2 : 4} visible={isMobile ? 2 : 3} scrollable={isMobile} />
              <Row projects={bottomRow} reverse onLightbox={setLightbox} duration={rowDuration(bottomRow.length)} perView={isMobile ? 2 : 4} visible={isMobile ? 2 : 3} scrollable={isMobile} />

            </>
          )}
        </div>

        <div className="mt-8 flex flex-wrap items-center justify-center gap-3 sm:mt-10 sm:justify-end">
          <Link
            to="/websites"
            onClick={() => clickCta("view_more_work", sectionId)}
            className="inline-flex cursor-pointer items-center gap-2 text-sm font-semibold text-foreground transition-colors hover:text-brand-blue sm:text-base"
          >
            View more work
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>

      <Lightbox project={lightbox} onOpenChange={(open) => !open && setLightbox(null)} />
    </section>
  );
}
